import { OverlayTrigger, Popover, Tooltip } from "react-bootstrap";
import React from "react";
import moment from "moment";
import { ButtonLoadingSpinner } from "../components/global/LoadingSpinner";
import apiHandler from "../js/apiHandler";
import Constants from "../js/constants";
import {
	InfoIcon,
	QuestionMarkIcon,
	ChevronLeftIcon,
	ChevronRightIcon,
	ExclamationMarkIcon,
	CollectionsIcon,
} from "../components/global/SvgIcons";

const HELPER_ICON_MAP = {
	"icon-info": InfoIcon,
	"icon-question-mark": QuestionMarkIcon,
};

// A value outside the dropdown is a negotiated rate: rendered read-only.
export const isStandardRentalPolicy = (rentalPolicy) =>
	(Constants.RENTAL_POLICIES || []).some((p) => p.value === rentalPolicy);

// Tier defaults may fill an empty policy, never overwrite a negotiated one.
export const canApplyTierRentalPolicy = (rentalPolicy) =>
	!rentalPolicy || isStandardRentalPolicy(rentalPolicy);

const DEFAULT_FLEXSTEP_MODEL = "full";

export const findAccountTierGroupName = (accountTiersConfig, tierName) => {
	if (!tierName) return null;
	const configObj = accountTiersConfig?.account_tier_default_settings || {};
	for (const groupName of Object.keys(configObj)) {
		const items = configObj[groupName];
		if (Object.prototype.hasOwnProperty.call(items, tierName)) {
			return groupName;
		}
	}
	return null;
};

export const getAccountTierDefinition = (accountTiersConfig, tierName) => {
	const groupName = findAccountTierGroupName(accountTiersConfig, tierName);
	if (!groupName) return null;
	const tierDef = accountTiersConfig?.account_tier_default_settings?.[groupName]?.[tierName];
	return tierDef ?? null;
};

/** Maps account tier `flexstep` from GET /accounttiers to FLEXStep API model; default `full`. */
export const normalizeFlexstepModelFromAccountTier = (flexstep) => {
	if (flexstep == null || flexstep === "") return DEFAULT_FLEXSTEP_MODEL;
	if (flexstep === "standard") return "full";
	if (flexstep === "lite" || flexstep === "weekday" || flexstep === "full") return flexstep;
	return DEFAULT_FLEXSTEP_MODEL;
};

export const resolveFlexstepModelForAccountTier = (accountTiersConfig, tierName) => {
	if (!tierName) return DEFAULT_FLEXSTEP_MODEL;
	const tierDef = getAccountTierDefinition(accountTiersConfig, tierName);
	if (!tierDef) return DEFAULT_FLEXSTEP_MODEL;
	return normalizeFlexstepModelFromAccountTier(tierDef.flexstep);
};

const mergeFlexstepConfigWithModel = (existingConfig, model) => {
	const base = existingConfig ? { ...existingConfig } : { enabled: true, excluded_property_ids: [] };
	const newConfig = {
		...base,
		enabled: base.enabled !== false,
		model,
		excluded_property_ids: base.excluded_property_ids || [],
	};
	if (model === "lite") {
		newConfig.booking_fee = base.booking_fee ?? "";
	} else {
		delete newConfig.booking_fee;
	}
	return newConfig;
};

export const applyFlexstepConfigForAccountTier = (existingConfig, accountTiersConfig, tierName) => {
	const model = resolveFlexstepModelForAccountTier(accountTiersConfig, tierName);
	return mergeFlexstepConfigWithModel(existingConfig, model);
};

// In-flight + cached organization-details fetches, keyed by org name. Multiple
// callers (e.g. StripeSetupTrigger, PropertyEditor) share the same network
// request rather than each issuing their own GET /organizations/{org}.
const _orgDetailsCache = new Map(); // org -> Promise<details>

/**
 * Fetch (and cache) the accapi organization details for the given org name.
 * Concurrent callers within the same session share a single in-flight request.
 * Pass `{ force: true }` to bypass the cache and re-fetch.
 * Returns the unwrapped details object (i.e. `response.data` or the response
 * itself if not wrapped). Throws on network/API error.
 */
export const fetchOrgDetails = (org, { force = false } = {}) => {
	if (!org) return Promise.resolve(null);
	if (!force && _orgDetailsCache.has(org)) return _orgDetailsCache.get(org);
	const p = apiHandler
		.get({
			apiName: "accapi",
			path: `/organizations/${org}`,
			options: { headers: { "Cache-Control": "must-revalidate" } },
		})
		.then((res) => res?.data || res)
		.catch((err) => {
			_orgDetailsCache.delete(org);
			throw err;
		});
	_orgDetailsCache.set(org, p);
	return p;
};

export const clearOrgDetailsCache = (org) => {
	if (org) _orgDetailsCache.delete(org);
	else _orgDetailsCache.clear();
};

export const isDevEnvironment = () =>
	typeof APIConfig !== "undefined" && APIConfig.ENV === "DEV"; // eslint-disable-line no-undef

// Permission helpers (migrated from permissions.js)
export const hasAll = (required = [], perms = []) => {
	if (!required || required.length === 0) return true;
	if (!Array.isArray(perms)) return false;
	return required.every((p) => perms.includes(p));
};

export const hasAny = (required = [], perms = []) => {
	if (!required || required.length === 0) return true;
	if (!Array.isArray(perms)) return false;
	return required.some((p) => perms.includes(p));
};

const ensureArray = (val) => (Array.isArray(val) ? val : [val]);

/**
 * PermissionGate wraps children and only renders when user has required permissions.
 * Props:
 * - permissions: string[] (current user's permissions)
 * - required: string | string[] (permissions required)
 * - mode: 'any' | 'all' (default 'any')
 * - fallback: ReactNode (optional)
 */
export const PermissionGate = ({
	permissions = [],
	required = [],
	mode = "any",
	fallback = null,
	children,
}) => {
	const req = ensureArray(required).filter(Boolean);
	const allowed = mode === "all" ? hasAll(req, permissions) : hasAny(req, permissions);
	if (!req.length || allowed) return <>{children}</>;
	return fallback ?? null;
};

export const checkPasswordRule = (password, rules) => {
	if (password.match(/(?=.*[a-z])/)) {
		rules.lowercase = true;
	} else {
		rules.lowercase = false;
	}
	if (password.match(/(.*[A-Z].*)/)) {
		rules.uppercase = true;
	} else {
		rules.uppercase = false;
	}
	if (password.match(/(?=.*\d)/)) {
		rules.number = true;
	} else {
		rules.number = false;
	}
	if (password.match(/[!@#$%^&*(),.?":{}|<>`+=\-_\[\]'\\/<>]/)) {
		rules.specialChar = true;
	} else {
		rules.specialChar = false;
	}
	if (password.length >= 8) {
		rules.minimum = true;
	} else {
		rules.minimum = false;
	}

	return rules;
};

export const getSessionId = () => {
	const maximum = 99999;
	const minimum = 10000;
	return Math.floor(Math.random() * (maximum - minimum + 1)) + minimum;
};

export const infoPopover = (
	message,
	title = null,
	placement = "top",
	iconName = "icon-info",
	func,
	params
) => {
	const Icon = HELPER_ICON_MAP[iconName];
	return (
		<span>
			<OverlayTrigger
				placement={placement}
				overlay={
					<Popover
						id="popover-basic"
						placement={placement}
						title={title ? title : null}>
						<p>{message}</p>
					</Popover>
				}>
				<div
					className="info-popover"
					onClick={func ? (e) => func(e, params) : null}>
					{Icon ? <Icon size={14} /> : <i className={iconName}></i>}
				</div>
			</OverlayTrigger>
		</span>
	);
};

export const questionPopover = (
	message,
	title = null,
	placement = "bottom",
	iconClass = "input-question-mark"
) => {
	return (
		<OverlayTrigger
			placement={placement}
			overlay={
				<Popover id="popover-basic" placement={placement} title={title}>
					<p>{message}</p>
				</Popover>
			}>
			<QuestionMarkIcon size={15} className={iconClass} />
		</OverlayTrigger>
	);
};

export const filterOrg = (orgTreeData, org) => {
	const organization_role_tree =
		orgTreeData[0].organization_name === "_global_"
			? orgTreeData[0].child_organizations
			: orgTreeData;
	let currOrg = {};

	const filterChildOrg = (x) => {
		for (let i = 0; x.length > i; i++) {
			if (x[i].organization_name === org) {
				currOrg = x[i];
				break;
			} else if (x[i].child_organizations?.length) {
				filterChildOrg(x[i].child_organizations);
			}
		}
	};

	for (let i = 0; organization_role_tree.length > i; i++) {
		if (organization_role_tree[i].organization_name === org) {
			currOrg = organization_role_tree[i];
			break;
		} else if (organization_role_tree[i].child_organizations?.length) {
			filterChildOrg(organization_role_tree[i].child_organizations);
		}
	}

	return currOrg;
};

const getAllOrgs = (parentOrg) => {
	let allOrgs = [];

	const childOrgs = (org) => {
		for (let i = 0; org.length > i; i++) {
			allOrgs.push(org[i].organization_name);

			if (org[i].child_organizations?.length) {
				childOrgs(org[i].child_organizations);
			}
		}
	};

	for (let i = 0; parentOrg.length > i; i++) {
		allOrgs.push(parentOrg[i].organization_name);

		if (parentOrg[i].child_organizations?.length) {
			childOrgs(parentOrg[i].child_organizations);
		}
	}

	return allOrgs;
};

export const flattenOrgTree = (nodes = []) => {
	const out = [];
	const seenIds = new Set();
	const walk = (arr = []) => {
		arr.forEach((node) => {
			if (!node || !node.organization_name) return;
			const { organization_name, title, organization_id } = node;
			// Only add if we haven't seen this organization_id before
			if (!seenIds.has(organization_id)) {
				seenIds.add(organization_id);
				out.push({ organization_name, title, organization_id });
			}
			if (Array.isArray(node.child_organizations) && node.child_organizations.length) {
				walk(node.child_organizations);
			}
		});
	};
	walk(Array.isArray(nodes) ? nodes : []);
	return out;
};

export const getOrgData = (user, org) => {
	let currOrg = {},
		allOrganizations = [];

	if (
		user &&
		user.organization_role_tree &&
		user.organization_role_tree.length
	) {
		currOrg = filterOrg(user.organization_role_tree, org);

		if (Object.keys(currOrg).length) {
			allOrganizations = getAllOrgs([currOrg]);
		}
	}

	return {
		allOrganizations,
		currOrg: currOrg,
	};
};

// Deep comparison utility function to replace lodash isEqual
export const isEqual = (a, b) => {
	// Strict equality & null checks
	if (a === b) return true;
	if (a == null || b == null) return false;
	// NaN case (NaN !== NaN in JS)
	if (a !== a && b !== b) return true;

	// Type mismatch
	if (typeof a !== typeof b) return false;

	// Primitives (string, number, boolean, symbol, bigint)
	if (typeof a !== "object") return a === b;

	// Date objects
	if (a instanceof Date || b instanceof Date) {
		return (
			a instanceof Date && b instanceof Date && a.getTime() === b.getTime()
		);
	}

	// Arrays
	const aIsArray = Array.isArray(a);
	const bIsArray = Array.isArray(b);
	if (aIsArray || bIsArray) {
		if (!aIsArray || !bIsArray) return false;
		if (a.length !== b.length) return false;
		for (let i = 0; i < a.length; i++) {
			if (!isEqual(a[i], b[i])) return false;
		}
		return true;
	}

	// Generic objects
	const keysA = Object.keys(a);
	const keysB = Object.keys(b);
	if (keysA.length !== keysB.length) return false;
	for (let key of keysA) {
		if (!Object.prototype.hasOwnProperty.call(b, key)) return false;
		if (!isEqual(a[key], b[key])) return false;
	}
	return true;
};

export const suggestReplyBtn = (data, index, loading, func, funcSelector) => {
	if (data && !data.length) {
		return (
			<button
				className={`suggest-reply__generate-btn primary-btn blue-btn${loading ? " disabled" : ""}`}
				disabled={loading}
				onClick={loading ? undefined : (e) => func(e, true)}>
				{loading ? (
					<ButtonLoadingSpinner size="sm" message="Generating..." />
				) : (
					<span>Generate Message</span>
				)}
			</button>
		);
	}

	return (
		<div className="suggest-reply__nav">
			<button
				className="suggest-reply__nav-btn primary-btn blue-btn"
				onClick={(e) => funcSelector(e, "back")}
				disabled={loading || index === 0}>
				<ChevronLeftIcon size={16} />
			</button>
			<button
				className="suggest-reply__nav-btn primary-btn blue-btn"
				onClick={(e) => funcSelector(e, "foward")}
				disabled={loading}>
				{loading ? (
					<ButtonLoadingSpinner size="sm" message={null} />
				) : (
					<ChevronRightIcon size={16} />
				)}
			</button>
		</div>
	);
};

/**
 * Formats error messages from API responses, especially handling Amplify REST errors
 * Shape observed in Amplify errors:
 * { statusCode: 404, headers: {...}, body: "{\"Code\":\"NotFoundError\",\"Message\":\"('Raw LOS data was not found for listing with id: 295661', -1)\"}" }
 * This function attempts to parse body, pull Message, and clean tuple-like formatting ('message', -1).
 *
 * @param {Object} error - The error object from API call
 * @returns {string} Formatted error message
 */
export const formatQuoteError = (error) => {
	let rawBody = error?.response?.body;
	let dataObj = error?.response?.data; // In case apiHandler normalized it
	let msg = "";

	// If dataObj already parsed
	if (dataObj && typeof dataObj === "object") {
		msg = dataObj.Message || dataObj.message || dataObj.Code || "";
	}

	// Parse raw body string if needed
	if (!msg && typeof rawBody === "string") {
		try {
			const parsed = JSON.parse(rawBody);
			msg = parsed.Message || parsed.message || parsed.Code || "";
		} catch (_) {
			// fallback: try to regex extract Message key
			const m = rawBody.match(/"Message"\s*:\s*"([^\"]+)"/);
			if (m) msg = m[1];
			else msg = rawBody;
		}
	}

	// Clean patterns like: ("text", -1) or ('text', -1)
	if (msg) {
		const tupleMatch = msg.match(/^\('?"?([^'"\)]+)["']?,?\s*-?\d+\)?$/);
		// More precise tuple pattern: ('Message here', -1)
		const preciseTuple = msg.match(/^\('([^']+)',\s*-?\d+\)$/);
		if (preciseTuple) {
			msg = preciseTuple[1];
		} else if (tupleMatch && tupleMatch[1].includes("was not found")) {
			// heuristic cleanup
			msg = tupleMatch[1];
		}
		msg = msg.replace(/^['"]|['"]$/g, "").trim();
	}

	return msg || "An unexpected error occurred while generating the quote.";
};

/**
 * Returns a tooltip component with the specified text
 * @param {string} id - The ID for the tooltip
 * @param {string} text - The text to display in the tooltip
 * @returns {React.ReactElement} Tooltip component
 */
export const getTooltip = (id, text) => {
	return (
		<Tooltip id={id} style={{ position: "fixed" }}>
			{text}
		</Tooltip>
	);
};

/**
 * Extracts a human-friendly API error message from various error shapes.
 * - Prefers response.data.Message, then response.data.message
 * - Then response.data.errors (RAM API validation shape: [{ code, message }])
 * - Then response.data, error.message, or String(error)
 * - If the message contains a quoted tuple like: UnknownError: ('text here', -1)
 *   it extracts the inner quoted text.
 * - Strips a trailing period and trims whitespace.
 *
 * @param {any} error The error object thrown by an API call
 * @param {string} fallback Fallback message if none can be derived
 * @returns {string} Cleaned, user-friendly message
 */
export const parseApiErrorMessage = (
	error,
	fallback = "An error occurred."
) => {
	let raw = null;
	// apiHandler's normalizeError attaches the parsed body as error.data when
	// error.response.data isn't set (e.g. Amplify errors) - check both.
	const data = error?.response?.data ?? error?.data;
	if (data?.Message) raw = data.Message;
	else if (data?.message) raw = data.message;
	else if (Array.isArray(data?.errors) && data.errors.length) {
		raw = data.errors
			.map((e) => (typeof e === "string" ? e : e?.message))
			.filter(Boolean)
			.join(" ");
	} else if (data) raw = data;
	else if (error?.message) raw = error.message;
	else raw = String(error);

	if (typeof raw !== "string") {
		if (raw && typeof raw === "object") {
			raw = raw.Message || raw.message || JSON.stringify(raw);
		} else {
			raw = String(raw);
		}
	}

	// Extract message from Python-style tuple: ("msg", -1) or ('msg', -1)
	// Greedy match ensures inner quotes (like 'Q&A') don't terminate early
	const tupleMatch =
		raw.match(/^\s*\(\s*"([\s\S]*)"\s*,\s*-?\d+\s*\)/) ||
		raw.match(/^\s*\(\s*'([\s\S]*)'\s*,\s*-?\d+\s*\)/);
	let msg = tupleMatch ? tupleMatch[1] : raw;
	// Remove trailing period
	msg = msg.replace(/\.$/, "").trim();

	return msg || fallback;
};

/**
 * Indices of seasonal cancellation policies whose date range overlaps another
 * policy's, used to flag conflicting rows (shared by EditOrganization.js,
 * CreateOrganization.js, and OrganizationDetails.js). Rows missing/invalid
 * dates are skipped, not flagged.
 */
export const getSeasonalOverlapIndices = (policies = []) => {
	const overlaps = new Set();
	for (let i = 0; i < policies.length; i++) {
		const left = policies[i];
		if (!left?.start_date || !left?.end_date) continue;
		const leftStart = moment(left.start_date);
		const leftEnd = moment(left.end_date);
		if (!leftStart.isValid() || !leftEnd.isValid()) continue;
		for (let j = i + 1; j < policies.length; j++) {
			const right = policies[j];
			if (!right?.start_date || !right?.end_date) continue;
			const rightStart = moment(right.start_date);
			const rightEnd = moment(right.end_date);
			if (!rightStart.isValid() || !rightEnd.isValid()) continue;
			if (leftStart.isSameOrBefore(rightEnd, "day") && rightStart.isSameOrBefore(leftEnd, "day")) {
				overlaps.add(i);
				overlaps.add(j);
			}
		}
	}
	return overlaps;
};

/**
 * Per-row { dates, policy } validation for a seasonal cancellation policy list.
 * `currentValidations[idx].policy` is preserved when present, so callers that
 * track "policy" errors separately (e.g. only at submit time) aren't clobbered.
 */
export const applySeasonalDateValidations = (policies = [], currentValidations = []) => {
	const overlaps = getSeasonalOverlapIndices(policies);
	return policies.map((policy, idx) => {
		const startValid = !!policy?.start_date && moment(policy.start_date).isValid();
		const endValid = !!policy?.end_date && moment(policy.end_date).isValid();
		const datesInvalid = !startValid || !endValid || overlaps.has(idx);
		return {
			...(currentValidations[idx] || { policy: false }),
			dates: datesInvalid,
		};
	});
};

/**
 * Returns an OverlayTrigger component with tooltip and child element
 * @param {string} tooltipId - The ID for the tooltip
 * @param {string} tooltipText - The text to display in the tooltip
 * @param {React.ReactNode} children - The child element to wrap with the OverlayTrigger
 * @param {string} placement - Tooltip placement (default: "top")
 * @returns {React.ReactElement} OverlayTrigger component with tooltip and child
 */
export const renderWithTooltip = (
	tooltipId,
	tooltipText,
	children,
	placement = "top"
) => {
	return (
		<OverlayTrigger
			popperConfig={{ strategy: "fixed" }}
			placement={placement}
			overlay={getTooltip(tooltipId, tooltipText)}>
			{children}
		</OverlayTrigger>
	);
};

/**
 * Persist the currently selected organization so it survives new tabs.
 * Uses localStorage (shared across tabs) and mirrors into sessionStorage
 * for any same-tab readers that still expect it there.
 */
export const persistOrganizationContext = ({
	org,
	orgTitle,
	orgId,
} = {}) => {
	if (!org || typeof window === "undefined") return;

	const write = (storage) => {
		if (!storage || typeof storage.setItem !== "function") return;
		storage.setItem("org", org);
		storage.setItem("orgTitle", orgTitle || "");
		storage.setItem("orgId", orgId ?? "");
	};

	try {
		write(window.localStorage);
		write(window.sessionStorage);
	} catch (_) {
		// Ignore storage errors (e.g., private mode)
	}
};

/**
 * Read the stored organization name, preferring localStorage (cross-tab)
 * and falling back to sessionStorage for older same-tab values.
 */
export const getStoredOrganizationName = () => {
	if (typeof window === "undefined") return null;
	try {
		return (
			window.localStorage.getItem("org") ||
			window.sessionStorage.getItem("org") ||
			null
		);
	} catch (_) {
		return null;
	}
};

/**
 * Find an organization by name within a user's organization_role_tree (including nested children)
 * and optionally persist it to localStorage with customizable keys to remove.
 *
 * Params:
 * - user: object containing organization_role_tree
 * - orgName: string organization_name to select
 * - options.storage: Storage-like object (defaults to localStorage via persistOrganizationContext)
 * - options.removeKeys: string[] list of keys to remove from sessionStorage (defaults to ["reservations-listing","properties-listing"])
 *
 * Returns: { org, orgTitle, orgId } | null when not found
 */
export const setPropertyOrg = ({
	user,
	orgName,
	storage,
	removeKeys = ["reservations-listing", "properties-listing"],
} = {}) => {
	if (!user || !orgName) return null;
	const tree = user.organization_role_tree || [];
	if (!Array.isArray(tree) || !tree.length) return null;

	// Reuse existing helper to find org by name (handles nested children)
	const found = filterOrg(tree, orgName) || {};
	if (!found || !found.organization_name) return null;

	const payload = {
		org: found.organization_name,
		orgTitle: found.title,
		orgId: found.organization_id || "",
	};

	// Persist org context (localStorage + sessionStorage). If a custom storage
	// is provided, write there too for callers/tests that inject one.
	persistOrganizationContext(payload);
	if (
		storage &&
		typeof storage.setItem === "function" &&
		storage !== window?.localStorage &&
		storage !== window?.sessionStorage
	) {
		try {
			storage.setItem("org", payload.org);
			storage.setItem("orgTitle", payload.orgTitle || "");
			storage.setItem("orgId", payload.orgId);
		} catch (_) {
			// Ignore storage errors (e.g., private mode)
		}
	}

	// Listing filters live in sessionStorage (tab-local UI state)
	if (Array.isArray(removeKeys) && typeof window !== "undefined") {
		try {
			removeKeys.forEach((k) => {
				if (k) window.sessionStorage.removeItem(k);
			});
		} catch (_) {
			// Ignore storage errors (e.g., private mode)
		}
	}

	return payload;
};
/**
 * Renders a payment account warning icon based on requirements severity
 * @param {Object} paymentAccountStatus - The payment account status object
 * @returns {JSX.Element|null} - Warning icon with appropriate severity class or null
 */
export const PaymentAccountWarningIcon = ({ paymentAccountStatus }) => {
	if (!paymentAccountStatus) return null;

	const hasCurrentlyDue = paymentAccountStatus.requirements?.currently_due?.length > 0;
	const hasEventuallyDue = paymentAccountStatus.requirements?.eventually_due?.length > 0;
	const hasPastDue = paymentAccountStatus.requirements?.past_due?.length > 0;
	const hasErrors = paymentAccountStatus.requirements?.errors?.length > 0;
	
	if (hasCurrentlyDue || hasPastDue || hasErrors) {
		return <ExclamationMarkIcon size={17} className="icon-exclamation-mark severity-critical" />;
	} else if (hasEventuallyDue) {
		return <ExclamationMarkIcon size={17} className="icon-exclamation-mark severity-warning" />;
	}
	
	return null;
};

export const LoadingDots = () => (
	<div className="loading-dots">
		<span />
		<span />
		<span />
	</div>
);

/**
 * EmptyState — a "no data" placeholder with icon, message, and optional sub-text.
 * Uses the .empty-state CSS class.
 *
 * Usage:
 *   <EmptyState message="No Support Tickets Found" icon="icon-question-mark" />
 *   <EmptyState message="No collections found." />                // defaults to icon-collections
 *
 * Props:
 *   message   – string  (required) primary message text
 *   subText   – string  optional secondary line below the message
 *   icon      – string  icon class name (default "icon-collections")
 *   className – string  extra class on the wrapper
 */
export const EmptyState = ({ message, subText, icon = "icon-collections", renderIcon, className = "" }) => (
	<div className={`empty-state${className ? ` ${className}` : ""}`}>
		{renderIcon ? (
			renderIcon()
		) : HELPER_ICON_MAP[icon] || icon === "icon-collections" ? (
			(() => {
				const Icon = HELPER_ICON_MAP[icon] || CollectionsIcon;
				return <Icon size={36} color="#d1d5db" className="empty-state__icon" />;
			})()
		) : (
			<i className={`empty-state__icon ${icon}`} aria-hidden="true" />
		)}
		<span>{message}</span>
		{subText && <span className="empty-state__sub">{subText}</span>}
	</div>
);